import React, { FC, useEffect } from 'react'
import { useParams, Route, Switch, useRouteMatch } from 'react-router'
import { Helmet } from 'react-helmet'
import { useQuery } from '@apollo/client'
import { ThemeProvider, ITheme } from '@chakra-ui/core'
import { identify, storefrontVisited, savedCart } from '@engyalo/storefront-events'
import {
  getStoreFrontVariables,
  getStoreFront,
  getStoreFront_orderConfig as IOrderConfig,
} from '@engyalo/storefront-types/lib/graphql/storefront'
import { IProductsPricedPerCollectionVariables, ICatalog } from '@engyalo/storefront-types/lib/graphql/product'
import { ICartDictionary, IConfig, IPreloadedCart, IOrder } from '@engyalo/storefront-types/lib/plugins/model'
import { useRecoilState, useRecoilValue } from 'recoil'
import { Catalogue } from '../containers/Catalogue'
import { Cart } from '../containers/Cart'
import { Product } from '../containers/Product'
import defaultTheme from '../containers/Catalogue/theme'
import { getCatalog, getStorefrontConfig } from '../queries/index'
import { cartState, configState, orderConfigState } from '../recoil/atoms'
import { parseTheme } from '../lib/utilities'
import { getCustomerCode, getFlatProductValues } from '../utils/analytics'
import { skipIfInPreviewMode } from '../utils/mode'
import {
  getCartFromPreloadedCartItem,
  getPreloadedCartFromSession,
  getProductFromPreloadedCart,
} from '../utils/preloaded-cart'
import { ErrorTypes, OrderConfigStatus } from '../lib/constants'
import Error from '../containers/Error'
import { cartStatsState } from '../recoil/selectors'
import { orderState } from '../recoil/atoms'

enum PRELOADED_CART_TYPE {
  PROMOTION = 'Promotion',
  REORDER = 'Re-order',
}

const DEFAULT_LOGO_URL = 'https://storage.googleapis.com/yalo-img/4k/logo-purple.png'
const NOT_FOUND_ERROR = '404'

export const CatalogueRoute: FC = () => {
  const { path } = useRouteMatch()
  const { customer, order } = useParams()
  const [config, setConfig] = useRecoilState<IConfig>(configState)
  const [cart, setCart] = useRecoilState(cartState)
  const [errorState, setErrorState] = React.useState<number | null>(null)
  const [orderConfig, setOrderConfig] = useRecoilState<IOrderConfig>(orderConfigState)
  const [theme, setTheme] = React.useState<ITheme>(defaultTheme)
  const { totalNum, totalPrice } = useRecoilValue(cartStatsState)
  const [globalOrder, setGlobalOrder] = useRecoilState<IOrder>(orderState)

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  const { loading: loadingConfig } = useQuery<getStoreFront, getStoreFrontVariables>(getStorefrontConfig, {
    variables: {
      name: customer,
      configId: order,
    },
    skip: !customer,
    onError: (error) => {
      const { graphQLErrors } = error

      const graphError = graphQLErrors[0]
      if (graphError) {
        const { extensions } = graphError

        if (extensions?.code === NOT_FOUND_ERROR) {
          setErrorState(ErrorTypes.TTL_ERROR)
        } else {
          setErrorState(ErrorTypes.GENERAL_ERROR)
        }
      } else {
        setErrorState(ErrorTypes.GENERAL_ERROR)
      }

      setConfig({
        ...config,
        logo: DEFAULT_LOGO_URL,
      })
    },
    onCompleted: (data) => {
      if (data.storefront && data.storefront.config) {
        const currentConfig = data.storefront.config[0] ?? {}
        const preloadedCart = getPreloadedCartFromSession(data.orderConfig)

        setConfig({
          logo: data.storefront.logo || DEFAULT_LOGO_URL,
          minAmount: data.storefront.minAmount || config.minAmount,
          maxAmount: data.storefront.maxAmount || config.maxAmount,
          customer: data.storefront.customer,
          favicon: currentConfig.favicon || config.favicon,
          currency: data.storefront.currency || config.currency,
          currencyPrefix: data.storefront.currencyPrefix || config.currencyPrefix,
          currencyFormat: data.storefront?.currencyFormat || config.currencyFormat,
          storefrontId: data.storefront.id || '',
          enabledOrderNote: data.storefront.enabledOrderNote || config.enabledOrderNote,
          enabledCustomCart: data.storefront.enabledCustomCart || config.enabledCustomCart,
          enabledProductDetail:
            data.storefront.enabledProductDetail === null
              ? config.enabledProductDetail
              : data.storefront.enabledProductDetail && config.enabledProductDetail,
          preloadedCart,
        })
        data.storefront.config.forEach((config) => {
          const { type } = config
          if (type === 'theme') {
            setTheme(parseTheme(config, defaultTheme))
          }
        })

        if (data.orderConfig) {
          const { status } = data.orderConfig
          setOrderConfig(data.orderConfig)

          if (status === OrderConfigStatus.PROCESSED) {
            setErrorState(ErrorTypes.ORDER_CREATED_ERROR)
          }
        } else {
          setErrorState(ErrorTypes.TTL_ERROR)
        }

        skipIfInPreviewMode(() => {
          const session = {
            userID: data.orderConfig?.userID ?? '',
            customer: data.storefront?.customer ?? '',
          }

          identify({
            session: {
              ...session,
              channel: data.orderConfig?.channelName ?? '',
              customerCode: getCustomerCode(data.orderConfig?.metadata ?? null),
            },
          })

          if (!preloadedCart) {
            storefrontVisited({
              session,
              storefront: {
                currency: data?.storefront?.currency || config.currency,
              },
            })
          }
        })
      }
    },
  })

  const { loading: loadingProducts, data } = useQuery<ICatalog, IProductsPricedPerCollectionVariables>(getCatalog, {
    variables: {
      name: customer,
      configId: order,
    },
    skip: !customer,
    returnPartialData: true,
    fetchPolicy: 'no-cache',
  })

  const loading = loadingConfig || loadingProducts

  useEffect(() => {
    if (!loading && config.preloadedCart) {
      const newCart: ICartDictionary = config.preloadedCart.reduce(
        (prev: ICartDictionary, { cartId, quantity, unitType }: IPreloadedCart) => {
          const product = getProductFromPreloadedCart(data?.catalog || [], cartId)
          if (product) {
            const generatedCart = getCartFromPreloadedCartItem(
              cart,
              { ...product, sizeDescription: unitType || null },
              quantity,
              false,
              cartId
            )
            return {
              ...prev,
              ...generatedCart,
            } as ICartDictionary
          }
          return prev
        },
        {}
      )
      setCart(newCart)

      const session = {
        userID: orderConfig?.userID ?? '',
        customer: config?.customer ?? '',
      }
      const getCartItemValues = getFlatProductValues(newCart)
      const { totalNum, totalPrice } = Object.values(newCart).reduce(
        (prev, cartItem) => ({
          totalNum: prev.totalNum + (cartItem?.quantity || 0),
          totalPrice: prev.totalPrice + (cartItem.price && cartItem.quantity ? cartItem.price * cartItem.quantity : 0),
        }),
        { totalNum: 0, totalPrice: 0 }
      )
      storefrontVisited({
        session,
        storefront: {
          currency: config.currency,
        },
        cart: {
          size: totalNum,
          value: Number(totalPrice),
          ids: getCartItemValues('id'),
          skus: getCartItemValues('sku'),
          names: getCartItemValues('name'),
          categories: getCartItemValues('category'),
          note: '',
          // TODO: change based on types set in order config metadata
          preloadedType: PRELOADED_CART_TYPE.REORDER,
        },
      })
    }
  }, [loading, config.preloadedCart, data?.catalog])

  const saveCartEvent = () => { // TODO: This segment event could be used if the save cart feature will be enabled again
    const getCartItemValues = getFlatProductValues(cart)
    savedCart({
      cart: {
        size: totalNum,
        value: Number(totalPrice),
        ids: getCartItemValues('id'),
        skus: getCartItemValues('sku'),
        names: getCartItemValues('name'),
        categories: getCartItemValues('category'),
        note: globalOrder.orderNote,
      },
      session,
    })
  }

  return (
    <ThemeProvider theme={theme}>
      {config.favicon && (
        <Helmet>
          <meta charSet="utf-8" />
          <link rel="icon" href={config.favicon} />
        </Helmet>
      )}

      {errorState ? (
        <Error type={errorState} />
      ) : (
        <Switch>
          <Route exact path={path}>
            <Catalogue loading={loading} products={data?.catalog || []} />
          </Route>
          <Route path={`${path}/cart`}>
            <Cart />
          </Route>
          <Route path={`${path}/product`}>
            <Product />
          </Route>
        </Switch>
      )}
    </ThemeProvider>
  )
}
